import React, { useState, useMemo } from "react";
import {
    Typography, Box, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Chip, IconButton, CircularProgress, TableSortLabel
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import UploadIcon from "@mui/icons-material/Upload";
import Button from "./atoms/Button";
import { formatOADateTime } from "../utils/dateUtils";

/**
 * Package version history + download section.
 * @param {{ packages: Array, canUpload: boolean, onUpload: Function, onDownload: Function, users: Array, title?: string }} props
 *   `title`, when given, renders the section heading on the same row as the
 *   upload button so the two stay aligned (COL-147).
 */
const PackageSection = ({ packages = [], canUpload = false, onUpload, onDownload, downloadingVersion, isUploading = false, users = [], title }) => {
    const [sortConfig, setSortConfig] = useState({ key: 'modifiedAt', direction: 'desc' });

    const resolveUserDisplayName = (modifiedBy) => {
        if (!modifiedBy || modifiedBy.toLowerCase() === "system") return "System";
        const found = users.find(u =>
            u.displayName === modifiedBy ||
            u.email === modifiedBy ||
            `${u.firstName} ${u.lastName}`.trim() === modifiedBy
        );
        if (found) {
            return `${found.displayName} (${found.role?.roleName || "Internal User"})`;
        }
        return modifiedBy;
    };

    const sortedPackages = useMemo(() => {
        const items = [...packages];
        items.sort((a, b) => {
            let aVal = a[sortConfig.key];
            let bVal = b[sortConfig.key];

            if (sortConfig.key === 'modifiedAt') {
                aVal = new Date(aVal || 0).getTime();
                bVal = new Date(bVal || 0).getTime();
            }

            if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
        return items;
    }, [packages, sortConfig]);

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    return (
        <Box data-testid="oa-package-section">
            <Box sx={{ display: "flex", justifyContent: title ? "space-between" : "flex-end", alignItems: "center", mb: 2 }}>
                {title && (
                    <Typography fontWeight={600} sx={{ color: '#1a1a2e', fontSize: '16px' }}>
                        {title}
                    </Typography>
                )}
                {canUpload && (
                    <Button
                        variant="secondary"
                        startIcon={isUploading ? <CircularProgress size={20} color="inherit" /> : <UploadIcon />}
                        onClick={onUpload}
                        disabled={isUploading}
                        data-testid="oa-upload-package-btn"
                    >
                        {isUploading ? "Uploading..." : "Upload Zip"}
                    </Button>
                )}
            </Box>
            {packages.length === 0 ? (
                <Typography color="text.secondary" sx={{ py: 2 }}>No packages generated yet.</Typography>
            ) : (
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ backgroundColor: "#F8FAFC" }}>
                                <TableCell sx={{ fontWeight: 500, fontSize: "14px", color: "#64748B", borderBottom: '1px solid #E2E8F0' }}>
                                    <TableSortLabel
                                        active={sortConfig.key === 'version'}
                                        direction={sortConfig.key === 'version' ? sortConfig.direction : 'asc'}
                                        onClick={() => handleSort('version')}
                                    >
                                        Files
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ fontWeight: 500, fontSize: "14px", color: "#64748B", borderBottom: '1px solid #E2E8F0' }}>
                                    Modified By
                                </TableCell>
                                <TableCell sx={{ fontWeight: 500, fontSize: "14px", color: "#64748B", borderBottom: '1px solid #E2E8F0' }}>
                                    <TableSortLabel
                                        active={sortConfig.key === 'modifiedAt'}
                                        direction={sortConfig.key === 'modifiedAt' ? sortConfig.direction : 'asc'}
                                        onClick={() => handleSort('modifiedAt')}
                                    >
                                        Modified On
                                    </TableSortLabel>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedPackages.map((pkg) => (
                                <TableRow key={pkg.version} hover sx={{ "&:hover": { backgroundColor: "#F8FAFC" } }} data-testid={`oa-package-row-v${pkg.version}`}>
                                    <TableCell sx={{ fontSize: "13px" }}>
                                        <Box
                                            sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                color: downloadingVersion === pkg.version ? '#4B5563' : '#005CB9',
                                                cursor: downloadingVersion === pkg.version ? 'default' : 'pointer',
                                                fontWeight: 500
                                            }}
                                            onClick={() => downloadingVersion !== pkg.version && onDownload?.(pkg.version)}
                                        >
                                            {downloadingVersion === pkg.version && (
                                                <Typography
                                                    component="span"
                                                    sx={{
                                                        fontSize: 'inherit',
                                                        fontWeight: 600,
                                                        color: '#4B5563',
                                                        mr: 0.5
                                                    }}
                                                >
                                                    Downloading
                                                </Typography>
                                            )}
                                            <Typography
                                                component="span"
                                                sx={{
                                                    fontSize: 'inherit',
                                                    fontWeight: 'inherit',
                                                    textDecoration: 'none'
                                                }}
                                            >
                                                {pkg.fileName || `v${pkg.version}.zip`}
                                            </Typography>
                                            {downloadingVersion === pkg.version && <CircularProgress size={16} sx={{ color: '#005CB9' }} />}
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ fontSize: "13px" }}>
                                        {resolveUserDisplayName(pkg.modifiedBy)}
                                    </TableCell>
                                    <TableCell sx={{ fontSize: "13px" }}>{pkg.modifiedAt ? formatOADateTime(pkg.modifiedAt) : "—"}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
};

export default PackageSection;
